import type { ApexOptions } from 'apexcharts'
import {
  resolveDisplay,
  type CardChart,
  type ChartCard,
} from '@/features/dashboard/api/schemas'
import { formatMonthYear } from '@/features/dashboard/format/format'
import type { Theme } from '@/shared/theme/ThemeContext'
import type { ChartPalette } from './chartTheme'
import {
  metricSeries,
  referenceColor,
  referenceSeries,
  seriesLabel,
  trendColor,
} from './series'
import { formatChartValue, seriesTooltipHtml } from './tooltipHtml'

/** How a deep-dive card is drawn: bars, a line, an area, or a map of markets. */
export type DeepDiveKind = 'bar' | 'line' | 'area' | 'map'

/**
 * The card's chart type (the deep dive's override, else Metabase's display) as
 * the UI draws it. Types it has no chart for (pie, scatter, waterfall...) are
 * drawn as an area for now.
 */
export const deepDiveKind = (
  card: Pick<ChartCard, 'chartType'>,
  chart: CardChart | null | undefined
): DeepDiveKind => {
  const type = chart?.chartType ?? card.chartType
  return type === 'bar' || type === 'line' || type === 'map' ? type : 'area'
}

type Row = { name: string; data: (number | null)[] }

/** What a deep-dive chart plots, derived from its card and the stub's drawing hints. */
export const deepDiveModel = (
  card: ChartCard,
  chart: CardChart | null | undefined
) => {
  const kind = deepDiveKind(card, chart)
  const stacked = chart?.stacked === true
  const metrics: Row[] = metricSeries(card.series).map(series => ({
    name: seriesLabel(series.name),
    data: card.categories.map((_, i) => series.data[i] ?? null),
  }))
  const references: Row[] = referenceSeries(card.series).map(series => ({
    name: seriesLabel(series.name),
    data: card.categories.map((_, i) => series.data[i] ?? null),
  }))
  // Stacking a line only offsets its strokes: stacked lines are drawn as areas.
  const drawnAs: Exclude<DeepDiveKind, 'map'> =
    kind === 'map' || (stacked && kind === 'line') ? 'area' : kind
  return {
    kind: drawnAs,
    stacked,
    metrics,
    references,
    labels: card.categories.map(category =>
      formatMonthYear(category, chart?.dateFormat === 'month')
    ),
    // Each category's sum of the metrics (a benchmark is never part of it).
    totals: card.categories.map((_, i) =>
      metrics.reduce((sum, series) => sum + (series.data[i] ?? 0), 0)
    ),
    showTotals: stacked && chart?.stackedTotals === true && metrics.length > 1,
    everyLabel: chart?.xLabels === true,
    // A chart with benchmark lines is Apex's "line" chart of typed series.
    apexType: references.length > 0 ? ('line' as const) : drawnAs,
    // Apex stacks only columns next to lines, so stacked areas under benchmark
    // lines are stacked here instead: each drawn as its running total.
    stacksItself: drawnAs === 'area' && stacked && references.length > 0,
  }
}

export type DeepDiveModel = ReturnType<typeof deepDiveModel>

/** Each series as the running total of those up to it (a gap stays a gap). */
const runningTotals = (rows: Row[]): Row[] =>
  rows.map((row, index) => ({
    name: row.name,
    data: row.data.map((value, i) =>
      value === null
        ? null
        : rows
            .slice(0, index + 1)
            .reduce((sum, earlier) => sum + (earlier.data[i] ?? 0), 0)
    ),
  }))

/**
 * The series Apex draws. Next to benchmark lines each names its type (Apex
 * mixes types only so); stacked areas under them are drawn largest first, so
 * the smaller ones show in front of the bigger.
 */
export const deepDiveSeries = (
  model: DeepDiveModel
): { name: string; type?: string; data: (number | null)[] }[] => {
  const hasReferences = model.references.length > 0
  const metrics = model.stacksItself
    ? runningTotals(model.metrics).reverse()
    : model.metrics
  const metricType = model.kind === 'bar' ? 'column' : model.kind
  return [
    ...metrics.map(row =>
      hasReferences ? { ...row, type: metricType } : { ...row }
    ),
    ...model.references.map(row => ({ ...row, type: 'line' })),
  ]
}

/** The colours of the metrics (in the card's order) and of the benchmarks. */
const deepDiveColors = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette
) => {
  const single =
    (model.kind === 'area' || model.kind === 'line') &&
    model.metrics.length === 1
  return {
    metrics: model.metrics.map((row, i) =>
      single
        ? trendColor(row.data, palette, resolveDisplay(card).lowerIsBetter)
        : (palette.series[i % Math.max(palette.series.length, 1)] ??
          palette.goal)
    ),
    references: model.references.map((_, i) => referenceColor(palette, i)),
  }
}

const ROTATE_FROM_LABELS = 8
const ROTATED_DEGREES = -45

export const deepDiveOptions = (
  card: ChartCard,
  model: DeepDiveModel,
  palette: ChartPalette,
  theme: Theme,
  height: number
): ApexOptions => {
  const { kind, stacked, references, labels } = model
  const isBar = kind === 'bar'
  const hasReferences = references.length > 0
  const { metrics: metricColors, references: referenceColors } = deepDiveColors(
    card,
    model,
    palette
  )
  // Listed as the series are drawn: reversed where the areas are.
  const colors = [
    ...(model.stacksItself ? [...metricColors].reverse() : metricColors),
    ...referenceColors,
  ]
  const single =
    (kind === 'area' || kind === 'line') && model.metrics.length === 1
  const value = (v: number | null, decimals = 1) =>
    v === null ? '' : formatChartValue(v, card.unit, card.format, decimals)
  // Bar labels and the stacked totals sit above the tallest point.
  const headroom = (isBar && !stacked) || model.showTotals

  return {
    chart: {
      background: 'transparent',
      toolbar: { show: false },
      fontFamily: "'N26', -apple-system, BlinkMacSystemFont, sans-serif",
      foreColor: palette.label,
      type: model.apexType,
      height,
      stacked: stacked && !model.stacksItself,
      zoom: { enabled: false },
      animations: { speed: 300 },
    },
    colors,
    theme: { mode: theme },
    grid: {
      borderColor: palette.grid,
      padding: headroom ? { top: 20 } : {},
    },
    legend: {
      show: model.metrics.length + references.length > 1,
      labels: { colors: palette.label },
      // Stacked areas are drawn in reverse; the legend reads as the card does.
      inverseOrder: model.stacksItself,
    },
    dataLabels: isBar
      ? {
          enabled: true,
          enabledOnSeries: model.metrics.map((_, i) => i),
          offsetY: stacked ? 0 : -14,
          style: {
            fontSize: '11px',
            fontWeight: 600,
            // Stacked segments carry their label inside, over their colour.
            colors: [stacked ? palette.card : palette.text],
          },
          background: { enabled: false },
          formatter: (v: string | number | number[]) =>
            typeof v === 'number' && v !== 0 ? value(v) : '',
        }
      : { enabled: false },
    plotOptions: {
      bar: {
        borderRadius: 6,
        columnWidth: '55%',
        dataLabels: { position: stacked ? 'center' : 'top' },
      },
    },
    stroke: hasReferences
      ? {
          curve: 'straight',
          width: [
            ...model.metrics.map(() => (isBar ? 0 : 2.5)),
            ...references.map(() => 2),
          ],
          dashArray: [
            ...model.metrics.map(() => 0),
            ...references.map(() => 6),
          ],
        }
      : isBar
        ? { show: false }
        : { curve: 'monotoneCubic', width: 2.5 },
    // A lone trend area fades toward the baseline; stacked bands keep a flat
    // opacity, since a gradient would fade them by the chart, not by the band.
    fill:
      kind === 'area'
        ? stacked
          ? { type: 'solid', opacity: theme === 'dark' ? 0.4 : 0.1 }
          : {
              type: 'gradient',
              gradient: {
                shadeIntensity: 0,
                opacityFrom: single ? 0.3 : 0.15,
                opacityTo: 0,
                stops: [0, 90, 100],
              },
            }
        : {},
    markers: isBar ? {} : { size: 0, hover: { size: 6 } },
    xaxis: {
      categories: labels,
      labels: {
        show: true,
        hideOverlappingLabels: !model.everyLabel,
        // Every label shown, so a long run is tilted to keep them apart.
        rotate:
          model.everyLabel && labels.length > ROTATE_FROM_LABELS
            ? ROTATED_DEGREES
            : 0,
        rotateAlways: model.everyLabel && labels.length > ROTATE_FROM_LABELS,
        style: { colors: palette.label },
      },
      axisBorder: { color: palette.grid },
      axisTicks: { color: palette.grid },
      tooltip: { enabled: false },
    },
    yaxis: {
      labels: {
        style: { colors: palette.label },
        formatter: (v: number) => value(v),
      },
    },
    annotations: model.showTotals
      ? {
          points: labels.map((label, i) => ({
            x: label,
            y: model.totals[i] ?? 0,
            marker: { size: 0 },
            label: {
              text: value(model.totals[i] ?? 0),
              borderWidth: 0,
              offsetY: -8,
              style: {
                fontSize: '11px',
                fontWeight: 600,
                color: palette.label,
                background: 'transparent',
              },
            },
          })),
        }
      : {},
    tooltip: {
      theme,
      shared: true,
      intersect: false,
      custom: ({ dataPointIndex }: { dataPointIndex: number }) =>
        seriesTooltipHtml({
          label: labels[dataPointIndex] ?? '',
          unit: card.unit,
          format: card.format,
          palette,
          rows: [
            ...model.metrics.flatMap((row, i) => {
              const v = row.data[dataPointIndex]
              return v === null || v === undefined
                ? []
                : [
                    {
                      // A lone metric is the card's own; naming it adds nothing.
                      ...(model.metrics.length > 1 && { label: row.name }),
                      value: v,
                      color: metricColors[i] ?? palette.goal,
                    },
                  ]
            }),
            ...references.flatMap((row, i) => {
              const v = row.data[dataPointIndex]
              return v === null || v === undefined
                ? []
                : [
                    {
                      label: row.name,
                      value: v,
                      color: referenceColors[i] ?? palette.goal,
                      dashed: true,
                    },
                  ]
            }),
          ],
        }),
    },
  }
}
